import { paths } from '../../../libs/init/paths.mjs';

export type SegmentPageMatch = {
  category: string;
  pageName: string;
};

export type SegmentPageRule = SegmentPageMatch & {
  /**
   * Documentation only — helps reviewers reason about overlap with other rules.
   * First matching rule wins; put narrower patterns above broader ones.
   */
  describe: string;
  match: (pathname: string) => boolean;
};

const rule = (
  describe: string,
  match: (pathname: string) => boolean,
  category: string,
  pageName: string,
): SegmentPageRule => ({ describe, match, category, pageName });

/**
 * Whitelisted Next.js `router.pathname` values (includes `[param]` segments, no `basePath`).
 * Routes not matched here do not emit Segment `page` calls.
 */
export const SEGMENT_PAGE_RULES: SegmentPageRule[] = [
  rule('Apps', (pathname) => pathname === paths.apps, 'Provider', 'Apps'),
  rule(
    'Check-in',
    (pathname) => pathname === paths.checkIn,
    'Patient',
    'Check-in',
  ),
  rule(
    'Wrong room',
    (pathname) => pathname === paths.wrongRoom,
    'Error',
    'Wrong room',
  ),
];

/**
 * Next `router.pathname` omits `basePath` but may include a trailing slash when
 * `trailingSlash` is enabled in production. Rules use slash-free paths from `paths.mjs`.
 */
export function normalizePathnameForSegment(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

export function resolveSegmentPage(pathname: string): SegmentPageMatch | null {
  const normalized = normalizePathnameForSegment(pathname);
  const ruleFound = SEGMENT_PAGE_RULES.find((entry) => entry.match(normalized));
  if (!ruleFound) {
    return null;
  }
  return { category: ruleFound.category, pageName: ruleFound.pageName };
}
